//
// Micro Frontend Iframe Messages
//
// These messages are sent between the windows of rind and the micro frontend iframes
// using `window.postMessage()`. The messages are used to trigger actions that should be taken by
// the receiving window.
//
import { z } from 'zod';

// This is the standard envelope for events. Subscribers to a specified type of event will have the
// event detail passed to their handler.
export const eventSchema = z.object({
  type: z.string(),
  detail: z.record(z.unknown()).optional(),
});
export type Event = z.infer<typeof eventSchema>;

// Any code can post messages to the window object. Message types are prefixed with this string to
// help filter out messages that are not meant for our apps.
const messageTypePrefix = 'bench:micro-frontend-iframe:';

// This is shape of the `data` property that will be attached to `MessageEvent` objects passed to
// `window.addEventListener('message', (messageEvent) => {...})`.
export const messageDataSchema = z.object({
  type: z.string(),
  detail: z.record(z.unknown()).optional(),
});
export type MessageData = z.infer<typeof messageDataSchema>;

// "Publish event" messages are sent from rind into the iframes to tell them to publish an
// event to their event bus.
export const publishEventMessageType = `${messageTypePrefix}publish-event`;
export const publishEventMessageSchema = messageDataSchema.extend({
  type: z.literal(publishEventMessageType),
  detail: z.object({
    event: eventSchema,
  }),
});
export type PublishEventMessage = z.infer<typeof publishEventMessageSchema>;

// "Broadcast event" messages are sent from the iframes to rind to tell it that an event
// was published in the iframe. Rind then broadcasts the event to all other iframes, and
// to any other subscribers in the app.
export const broadcastEventMessageType = `${messageTypePrefix}broadcast-event`;
export const broadcastEventMessageSchema = messageDataSchema.extend({
  type: z.literal(broadcastEventMessageType),
  detail: z.object({
    event: eventSchema,
  }),
});
export type BroadcastEventMessage = z.infer<typeof broadcastEventMessageSchema>;

// "Load micro frontend" messages are sent from rind into the iframes to tell them to load
// a micro frontend with the given name and data. This is sent after the iframe has sent a
// `host-app-ready` message to rind, and also when the `name` or `data` props of the
// micro frontend component change.
export const loadMicroFrontendMessageType = `${messageTypePrefix}load-micro-frontend`;
export const loadMicroFrontendMessageSchema = messageDataSchema.extend({
  type: z.literal(loadMicroFrontendMessageType),
  detail: z.object({
    props: z.object({
      name: z.string(),
      data: z.record(z.unknown()).optional(),
      env: z.record(z.unknown()).optional(),
    }),
  }),
});
export type LoadMicroFrontendMessage = z.infer<typeof loadMicroFrontendMessageSchema>;

// "Content changed height" messages are sent from the iframes to rind to inform it that
// the height of the content inside the iframe has changed. The app can then resize the
// iframe to fit the content.
export const contentChangedHeightMessageType = `${messageTypePrefix}content-changed-height`;
export const contentChangedHeightMessageSchema = messageDataSchema.extend({
  type: z.literal(contentChangedHeightMessageType),
  detail: z.object({
    height: z.number(),
  }),
});
export type ContentChangedHeightMessage = z.infer<typeof contentChangedHeightMessageSchema>;

// "Host app ready" messages are sent from the iframes to rind to inform it that the
// iframe is ready to receive a `load-micro-frontend` message. This is sent after the iframe has
// registered an event listener for `window.postMessage()` calls.
export const hostAppReadyMessageType = `${messageTypePrefix}host-app-ready`;
export const hostAppReadyMessageSchema = messageDataSchema.extend({
  type: z.literal(hostAppReadyMessageType),
});
export type HostAppReadyMessage = z.infer<typeof hostAppReadyMessageSchema>;
